/* @flow */
/* eslint-env es6,browser */

import adconfig from '../adconfig';
import { logError } from '../utils/logger';
import { extend } from '../utils/coreutils';
import { loadJS, getEvaluatedDialup } from '../utils/browserutil';

/**
 * Provides interface for all ad servers and common functionality
 *
 * @class
 * @requires  module:utils
 * @requires  module:adconfig
 * @requires module:logger
 */

class _adserverbase {
  _doc: Document;
  _win: window;
  _name: string;
  _isDialup: boolean;
  _brand: string;
  _QPVars: Object;
  _defConfig: $Subtype<_perAdServerConfig>;
  _config: $Subtype<_perAdServerConfig>;
  constructor() {
    this._doc = document;
    this._win = window;
    this._isDialup = false;
    this._brand = '';
    this._QPVars = {};
    this._defConfig = {
      lib: '',
      timeout: 10000
    };
    this._config = extend(true, this._defConfig);
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @typedef {object} globalParams
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._isDialup = g.isDialup;
    this._brand = g.phInfo.brand;
    this._QPVars = g.QPVars;
  }

  _preUpdateConfig() {
    //Dummy funcion to be overidden in child class if needed
  }

  /**
   * Update config - called from adserver updateConfig
   *
   * @param {_perAdServerConfig} o - adserver config from adconfig
   * @returns {undefined}
   */
  updateConfig(o: _perAdServerConfig) {
    this._preUpdateConfig();
    this._config = extend(true, {}, this._defConfig, o);
    this._postUpdateConfig();
  }

  _postUpdateConfig() {
    //Dummy funcion to be overidden in child class if needed
  }

  /**
   * getName - getter for name
   *
   * @returns {string} name
   */
  getName(): string {
    return this._name;
  }

  _getCustomVars(
    pos: string,
    o: hbPartnerData,
    customVars: Object,
    bRotation: boolean
  ) {
    const params = o && o[pos] ? o[pos] : {};
    return extend(
      true,
      {
        UOL_POS: pos,
        UOL_PAGENAME: adconfig.getPageName(),
        UOL_ADROTATE: bRotation
      },
      customVars,
      params._CV,
      {
        _QP: this._getQueryParams(pos, o, bRotation)
      }
    );
  }

  /**
   * Get ad server parameter
   *
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  _getOpenxParams(bRotation: boolean) {
    return extend(true, {}, this._QPVars, {
      uolpagename: adconfig.getPageName(),
      uoldialup: getEvaluatedDialup(this._isDialup),
      uolbrand: this._brand,
      uolrotate: bRotation
    });
  }

  _postInit() {
    /* dummy func for post init after library is loaded*/
  }

  _getQueryParams(pos: string, o: hbPartnerData, bRotation: boolean) {
    return extend(
      true,
      {},
      o && o[pos] ? o[pos]._QP : {},
      this._getOpenxParams(bRotation)
    );
  }

  _loadLibrary(libPath: string = '') {
    if (!libPath.length) {
      return;
    }
    loadJS(libPath, this._doc); //Load Ad server library
  }

  _isSupported() {
    return true;
  }

  clearAdRequests(): void {
    /* dummy func for post init after library is loaded*/
  }

  /**
   * Loads openx static using adloader
   *
   * @param {Array<string>} posList - position list array
   * @param {object.<string, hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  /* eslint-disable no-unused-vars */
  loadAds(
    posList: Array<string>,
    o: hbPartnerData,
    customVars: Object,
    bRotation: boolean
  ) {
    /* eslint-enable no-unused-vars */
    //Ideally, Base class loadAds() should not be called. Each child class should override it.
    logError(`${this._name} Do not class base class loadAds()`);
  }
}

export default _adserverbase;
